import { logger } from '@adornis/base/logging.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import type { XInput } from '@adornis/forms/x-input.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { passwordRegexCheck } from '@adornis/users/db/validators.js';
import { getLogo } from '@ils/examType/logos.js';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { filter, switchMap } from 'rxjs';
import { ILSUser } from '../../db/ils-user.js';

@customElement('ils-reset-password')
export class ILSResetPassword extends ChemistryLitElement {
  override styles() {
    return [
      {
        ':host': {
          width: '100%',
          height: '100%',
        },
      },
    ];
  }

  @state() public changeToken = new RXController(
    this,
    routingState.searchParams.get('token').pipe(
      filter(Boolean),
      switchMap(async token => {
        const isValid = await ILSUser.checkPasswordChangeToken(token)();
        if (!isValid) this.step = -1;
        routingState.searchParams.delete('token');
        return token;
      }),
    ),
  );
  @state() public newPassword?: string;
  @state() public passwordRepeat?: string;

  @state() public step = 0;

  override render() {
    return html`
      <x-flex padding="md" space="lg" center crossaxis-center ${css({ height: '100%' })}>
        <img src=${getLogo('big')} alt="Logo" ${css({ maxWidth: '200px' })} />
        ${this._stepContent()}
      </x-flex>
    `;
  }

  private _stepContent() {
    if (this.step === 0) return this.changePasswortScreen();
    if (this.step === 1) return this.changeSuccessfulScreen();
    return this.linkExpiredScreen();
  }

  private changePasswortScreen() {
    return html`
      <x-flex space="md">
        <x-h2> Neues Passwort anlegen </x-h2>
        <x-text tone="subtle" ${css({ maxWidth: this.sizes.maxReadableWidth })}>
          Das Passwort muss mindestens 8 Zeichen lang sein, ein Groß- und Kleinbuchstaben, eine Zahl und ein
          Sonderzeichen beinhalten.
        </x-text>
        <x-input
          placeholder="Neues Passwort"
          type="password"
          @value-picked=${(e: CustomEvent) => {
            const elem = e.currentTarget as XInput;
            const value = e.detail.value;
            if (value && !passwordRegexCheck(value))
              elem.error =
                'Das Passwort muss mindestens 8 Zeichen, einen Großbuchstaben und ein Sonderzeichen beinhalten.';
            else {
              elem.error = undefined;
              this.newPassword = value;
            }
          }}
        ></x-input>
        <x-input
          placeholder="Passwort wiederholen"
          type="password"
          @value-picked=${(e: CustomEvent) => {
            const elem = e.currentTarget as XInput;
            const value = e.detail.value;
            if (value && value !== this.newPassword) elem.error = 'Die Passwörter stimmen nicht überein.';
            else {
              elem.error = undefined;
              this.passwordRepeat = value;
            }
          }}
        ></x-input>
        <x-button
          @click=${async () => {
            try {
              if (this.changeToken.value && this.passwordRepeat) {
                const changeResult = await XDialog.waitFor(
                  ILSUser.changePasswordWithToken(this.changeToken.value, this.passwordRepeat)(),
                );
                if (changeResult) {
                  void XSnackbar.show('Passwort wurde erfolgreich geändert.');
                  this.step = 1;
                }
              }
            } catch (e) {
              const error = e as Error;
              logger.error(error);
              void XDialog.alert(error.message);
            }
          }}
        >
          Passwort ändern
        </x-button>
      </x-flex>
    `;
  }

  private changeSuccessfulScreen() {
    return html`
      <x-flex space="lg">
        <x-h1> Ihr Passwort wurde erfolgreich zurückgesetzt </x-h1>
        <x-button href="/"> zurück </x-button>
      </x-flex>
    `;
  }

  private linkExpiredScreen() {
    return html`
      <x-flex padding="lg" space="md" crossaxis-center>
        <x-h3>
          Dieser Link ist bereits abgelaufen. Falls Sie Ihr Passwort ändern möchten müssen Sie einen neuen anfordern.
        </x-h3>
        <x-button href="/"> zurück </x-button>
      </x-flex>
    `;
  }
}
